import { useEffect, useRef } from "react";

import {
  useGetUserConsentQuery,
  useSaveUserConsentMutation,
} from "@/features/cookieConsent/cookieConsent.api";
import { hydrateConsentFromServer } from "@/features/cookieConsent/cookieConsent.slice";
import { useAppDispatch, useAppSelector } from "@/redux/store";

/**
 * Sync local consent state with backend for authenticated users.
 * @param {boolean} isAuthenticated Whether the current user session is authenticated.
 * @returns {void}
 */
export function useSyncConsent(isAuthenticated: boolean): void {
  const dispatch = useAppDispatch();
  const { consentGiven, lastUpdated, consentVersion } = useAppSelector(
    (state) => state.cookieConsent,
  );

  const {
    data: serverConsent,
    isFetching,
    isLoading,
    isUninitialized,
  } = useGetUserConsentQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [saveUserConsent] = useSaveUserConsentMutation();
  const isInitializedRef = useRef(false);
  const lastSyncedPayloadRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      isInitializedRef.current = false;
      lastSyncedPayloadRef.current = null;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isUninitialized || isLoading || isFetching) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    const localPayload = {
      consentGiven,
      lastUpdated: lastUpdated ?? undefined,
      consentVersion,
    };

    const localPayloadKey = JSON.stringify(localPayload);

    if (!isInitializedRef.current) {
      isInitializedRef.current = true;

      if (serverConsent) {
        const serverTime = serverConsent.lastUpdated
          ? new Date(serverConsent.lastUpdated).getTime()
          : 0;
        const localTime = lastUpdated ? new Date(lastUpdated).getTime() : 0;

        if (
          serverConsent.consentGiven &&
          (!consentGiven || serverTime >= localTime)
        ) {
          const serverPayload = {
            consentGiven: serverConsent.consentGiven,
            lastUpdated: serverConsent.lastUpdated,
            consentVersion: serverConsent.consentVersion,
          };

          dispatch(hydrateConsentFromServer(serverPayload));
          lastSyncedPayloadRef.current = JSON.stringify(serverPayload);
        } else if (consentGiven && localTime > serverTime) {
          void saveUserConsent(localPayload);
          lastSyncedPayloadRef.current = localPayloadKey;
        }

        return;
      }

      if (consentGiven) {
        void saveUserConsent(localPayload);
        lastSyncedPayloadRef.current = localPayloadKey;
      }

      return;
    }

    if (consentGiven && localPayloadKey !== lastSyncedPayloadRef.current) {
      void saveUserConsent(localPayload);
      lastSyncedPayloadRef.current = localPayloadKey;
    }
  }, [
    isAuthenticated,
    isUninitialized,
    isLoading,
    isFetching,
    serverConsent,
    consentGiven,
    lastUpdated,
    consentVersion,
    dispatch,
    saveUserConsent,
  ]);
}
